import { useCallback, useMemo } from 'react';

import type {
	PerimeterXEnforcerChallenge,
	PerimeterXEnforcerChallengeWithBlockedUrl,
} from '@change-corgi/core/perimeterx';
import { useTracking } from '@change-corgi/core-react/tracking';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { usePerimeterXCaptcha } from '#src/app/shared/components/perimeterx';

export function useAuthMethodPerimeterXCaptcha(): ({
	pxResponse,
}: {
	pxResponse: PerimeterXEnforcerChallengeWithBlockedUrl;
}) => Promise<void> {
	const { trackFailure, trackSuccess, trackShow } = usePerimeterXTracking();

	const triggerCaptcha = usePerimeterXCaptcha();

	return useCallback(
		async ({ pxResponse }) => {
			trackShow();
			const result = await triggerCaptcha({ pxResponse });
			if (result) {
				trackSuccess(pxResponse);
			} else {
				trackFailure(pxResponse);
			}
		},
		[triggerCaptcha, trackShow, trackFailure, trackSuccess],
	);
}

function usePerimeterXTracking() {
	const track = useTracking();

	const getProps = useCallback(
		(pxResponse: PerimeterXEnforcerChallenge): TrackingProperties => ({
			form: 'login_or_signup_auth_method',
			pxRefId: pxResponse.uuid,
			pxVid: pxResponse.vid,
		}),
		[],
	);

	return useMemo(
		() => ({
			trackShow: () => {
				void track('px_captcha_shown', { form: 'login_or_signup_auth_method' });
			},
			trackSuccess: (pxResponse: PerimeterXEnforcerChallenge) => {
				void track('px_captcha_solved', getProps(pxResponse));
			},
			trackFailure: (pxResponse: PerimeterXEnforcerChallenge) => {
				void track('px_captcha_failed', getProps(pxResponse));
			},
		}),
		[track, getProps],
	);
}
